// src/config/urls.js
// ======================================================
// 🔗 CONFIGURACIÓN CENTRALIZADA DE URLs - V15 ENTERPRISE
// ======================================================
// ✅ V15: Anti-bucles — /p/imovel para SSR, /imovel para SPA
// ✅ V14: BASE_URL dinámico multi-dominio (Enterprise)
// ✅ V14: Soporte white-label automático
// ✅ V14: Zero breaking changes - backward compatible 100%
// ✅ V13: URLs públicas incluyen companyId + userId
// ✅ V13: Aislamiento total entre cuentas
// ✅ V13: Compatible con PublicProperty V13 + CloudStrategy V8
// ======================================================

const isProduction = import.meta.env.PROD;

/**
 * V14 ENTERPRISE: BASE_URL dinámico multi-dominio
 * 
 * En producción: usa window.location.origin (dominio actual)
 * En desarrollo: usa localhost:5178
 * 
 * Beneficios:
 * - Soporta múltiples dominios sin tocar código
 * - White-label ready
 * - Si un dominio falla, el otro sigue funcionando
 * - Compatible con propzyn.com.br, app.ilhaleads.com.br, etc.
 */
const getBaseUrl = () => {
  if (!isProduction) return 'http://localhost:5178';
  
  // En producción, usar el dominio actual que está accediendo el usuario
  try {
    if (typeof window !== 'undefined' && window.location) {
      return window.location.origin;
    }
  } catch (e) {
    // SSR o entorno sin window
  }
  
  // Fallback seguro si no hay window (nunca debería pasar en producción real)
  return 'https://app.ilhaleads.com.br';
};

const BASE_URL = getBaseUrl();

/**
 * V13: Obtiene el companyId desde múltiples fuentes
 * Prioridad: localStorage → sessionStorage → URL params → null
 */
const getCompanyId = () => {
  try {
    // Intentar obtener de auth_user en localStorage
    const authUser = localStorage.getItem('auth_user');
    if (authUser) {
      const parsed = JSON.parse(authUser);
      if (parsed.companyId) return parsed.companyId;
      if (parsed.company) return parsed.company;
    }
    
    // Intentar obtener de sessionStorage
    const sessionCompany = sessionStorage.getItem('current_company_id');
    if (sessionCompany) return sessionCompany;
    
    // Intentar obtener de last_viewed_companyId
    const lastViewed = localStorage.getItem('last_viewed_companyId');
    if (lastViewed) return lastViewed;
    
    return null;
  } catch {
    return null;
  }
};

/**
 * V13: Obtiene el userId desde múltiples fuentes
 */
const getUserId = () => {
  try {
    const authUser = localStorage.getItem('auth_user');
    if (authUser) {
      const parsed = JSON.parse(authUser);
      return parsed.uid || parsed.id || null;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * V13: Construye parámetros de seguridad para URLs públicas
 * Incluye companyId y userId para aislamiento total
 */
const buildSecurityParams = (property) => {
  const params = new URLSearchParams();
  
  // Obtener companyId (prioridad: propiedad → localStorage → null)
  let cid = null;
  if (property) {
    cid = property.companyId || property.company || property._tenantId;
  }
  if (!cid) cid = getCompanyId();
  if (cid) params.append('cid', cid);
  
  // Obtener userId (prioridad: propiedad → localStorage → null)
  let uid = null;
  if (property) {
    uid = property.userId || property.createdBy || property.ownerId;
  }
  if (!uid) uid = getUserId();
  if (uid) params.append('uid', uid);
  
  return params.toString();
};

/**
 * V15: URL pública de un inmueble específico (ANTI-BUCLES)
 * /p/imovel/:id → SSR (para compartir en WhatsApp/Facebook)
 * /imovel/:id  → SPA (para navegación normal)
 * Incluye companyId y userId para aislamiento total
 * Ahora con BASE_URL dinámico multi-dominio
 * 
 * @param {string|object} idOrProperty - ID del inmueble o objeto propiedad completo
 * @returns {string} URL pública con parámetros de seguridad
 * 
 * Ejemplo de uso:
 *   getPublicPropertyUrl('prop123') 
 *   → https://propzyn.com.br/p/imovel/prop123?cid=comp456&uid=user789
 *   → https://app.ilhaleads.com.br/p/imovel/prop123?cid=comp456&uid=user789
 */
export const getPublicPropertyUrl = (idOrProperty) => {
  // Si es un objeto propiedad completo
  if (typeof idOrProperty === 'object' && idOrProperty !== null) {
    const property = idOrProperty;
    if (!property.id) return `${BASE_URL}/imoveis`;
    const params = buildSecurityParams(property);
    return `${BASE_URL}/p/imovel/${property.id}${params ? `?${params}` : ''}`;
  }
  
  // Si es solo un ID string
  const id = idOrProperty;
  if (!id) return `${BASE_URL}/imoveis`;
  const params = buildSecurityParams(null);
  return `${BASE_URL}/p/imovel/${id}${params ? `?${params}` : ''}`;
};

/**
 * V14: URL pública del listado de inmuebles
 * Incluye companyId para filtrar por compañía
 * Ahora con BASE_URL dinámico multi-dominio
 * 
 * @param {string} companyId - ID de la compañía (opcional)
 * @returns {string} URL del listado público
 * 
 * Ejemplo:
 *   getPublicPropertiesUrl('comp456')
 *   → https://propzyn.com.br/imoveis?cid=comp456&uid=user789
 */
export const getPublicPropertiesUrl = (companyId = null) => {
  const params = new URLSearchParams();
  
  let cid = companyId || getCompanyId();
  if (cid) params.append('cid', cid);
  
  let uid = getUserId();
  if (uid) params.append('uid', uid);
  
  const queryString = params.toString();
  return `${BASE_URL}/imoveis${queryString ? `?${queryString}` : ''}`;
};

/**
 * URL de administración - Listado de inmuebles
 */
export const getAdminPropertiesUrl = () => {
  return `/admin/imoveis`;
};

/**
 * URL de administración - Inmueble específico
 * 
 * @param {string} id - ID del inmueble
 * @returns {string} URL de admin
 */
export const getAdminPropertyUrl = (id) => {
  if (!id) return `/admin/imoveis`;
  return `/admin/imoveis/${id}`;
};

/**
 * V14: URL para compartir una tarea
 * Ahora con BASE_URL dinámico multi-dominio
 * 
 * @param {string} taskId - ID de la tarea
 * @returns {string} URL de la tarea
 */
export const getTaskShareUrl = (taskId) => {
  if (!taskId) return `${BASE_URL}/tarefas`;
  return `${BASE_URL}/tarefa/${taskId}`;
};

/**
 * V14: URL del perfil público de la compañía (white label)
 * Ahora con BASE_URL dinámico multi-dominio
 * 
 * @param {string} companyId - ID de la compañía
 * @returns {string} URL pública de la compañía
 */
export const getPublicCompanyUrl = (companyId) => {
  if (!companyId) return BASE_URL;
  return `${BASE_URL}/imoveis?cid=${companyId}`;
};

/**
 * V15: Construye una URL de WhatsApp con mensaje predefinido
 * Ahora usa /p/imovel/:id para SSR y preview en WhatsApp
 * Ahora con URLs dinámicas multi-dominio
 * 
 * @param {object} property - Objeto propiedad completo
 * @param {object} company - Datos de la compañía (opcional)
 * @param {string} clientName - Nombre del cliente (opcional)
 * @returns {string} URL de WhatsApp
 */
export const getWhatsAppShareUrl = (property, company = null, clientName = 'Cliente') => {
  if (!property) return 'https://wa.me/';
  
  const publicUrl = getPublicPropertyUrl(property);
  const title = property.title || 'Imóvel';
  const price = property.price 
    ? `R$ ${Number(property.price).toLocaleString('pt-BR')}` 
    : 'Consultar';
  
  const message = encodeURIComponent(
    `*Olá!* 👋\n\n` +
    `Vi este imóvel e fiquei interessado! 🏠\n\n` +
    `📌 *Imóvel:* ${title}\n` +
    `💰 *Valor:* ${price}\n` +
    `\n📎 *Link:* ${publicUrl}\n\n` +
    `Poderia me passar mais informações?`
  );
  
  const phone = company?.telefoneWhatsApp || 
                company?.telefoneCelular || 
                company?.telefone || 
                '';
  let cleanPhone = phone.replace(/\D/g, '');
  if (cleanPhone.length >= 10 && !cleanPhone.startsWith('55')) {
    cleanPhone = '55' + cleanPhone;
  }
  const finalPhone = cleanPhone.length >= 12 ? cleanPhone : '';
  
  return finalPhone 
    ? `https://wa.me/${finalPhone}?text=${message}`
    : `https://wa.me/?text=${message}`;
};

// ======================================================
// 📤 EXPORTACIONES PARA COMPATIBILIDAD
// ======================================================

export default {
  getPublicPropertyUrl,
  getPublicPropertiesUrl,
  getAdminPropertiesUrl,
  getAdminPropertyUrl,
  getTaskShareUrl,
  getPublicCompanyUrl,
  getWhatsAppShareUrl,
  BASE_URL
};